import {useAuth} from "~/auth/useAuth";
import {PartnerDomain} from "@bounce/shared/consumer/declarations";
import axios, {AxiosInstance} from "axios";
import {PropsWithChildren, useEffect, useRef, useState} from "react";
import {consumerAppBaseUrl, happyMoneyBaseUrl} from "~/constants/defaultValues";
import {addRequestIdToQuery, createAxiosInstanceProxy, setAuthTokenInHeader} from "@bounce/shared/requests";

const axiosDefaultConsumerInstance = axios.create({
  baseURL: consumerAppBaseUrl,
});

export const axiosHappyMoneyInstance = axios.create({
  baseURL: happyMoneyBaseUrl,
});

const mapPartnerDomainToAxios: Partial<Record<PartnerDomain, AxiosInstance>> = {
  [PartnerDomain.HAPPY_MONEY]: axiosHappyMoneyInstance,
};

let internalAxiosConsumerInstance: AxiosInstance = axiosDefaultConsumerInstance;

export const axiosConsumerAppInstance = createAxiosInstanceProxy(() => internalAxiosConsumerInstance);

export const initAxiosConsumerAppInstance = (partnerDomain: string): void => {
  internalAxiosConsumerInstance =
    mapPartnerDomainToAxios[partnerDomain as PartnerDomain] ?? axiosDefaultConsumerInstance;
};

const ConsumerApiManager = ({children}: PropsWithChildren) => {
  const {token, partnerDomain} = useAuth();
  const isInitialized = useRef(false);
  const [axiosReady, setAxiosReady] = useState(false);
  const instances = [axiosConsumerAppInstance];

  useEffect(() => {
    if (!partnerDomain || isInitialized.current) return;

    initAxiosConsumerAppInstance(partnerDomain);
    isInitialized.current = true;

    [axiosDefaultConsumerInstance, axiosHappyMoneyInstance].forEach((instance) => addRequestIdToQuery(instance));

    setAxiosReady(true);
  }, [partnerDomain]);

  useEffect(() => {
    if (axiosReady) {
      setAuthTokenInHeader(token, instances);
    }
  }, [token, axiosReady]);

  if (!axiosReady) return null;

  return <>{children}</>;
};

export {ConsumerApiManager};
